import { z } from "zod"

const amount = z.number().int().nonnegative()
export const creditTermsSchema = z.object({
  version: z.string(),
  text: z.string(),
  interest: z.literal(0),
  fees: z.literal(0),
})
export const creditDebtSchema = z.object({
  id: z.string(),
  order_id: z.string(),
  amount,
  paid: amount,
  credited: amount,
  refunded: amount,
  outstanding: amount,
  refund_due: amount,
  due_date: z.string(),
  confirmed_at: z.string(),
  term_days: z.number(),
  terms_version: z.string(),
  terms_copy: z.string(),
  accepted_at: z.string(),
  holder_rut: z.string(),
})
export const creditSchema = z.object({
  account: z.object({
    id: z.string(),
    holder_name: z.string(),
    holder_rut: z.string(),
    holder_type: z.enum(["person", "company"]),
    credit_limit: amount,
    term_days: z.number(),
    status: z.enum(["approved", "suspended"]),
  }),
  available: amount,
  used: amount,
  reserved: amount,
  overdue: amount,
  over_limit: amount,
  unallocated: amount,
  projected_due_date: z.string(),
  terms: creditTermsSchema,
  debts: z.array(creditDebtSchema),
  receipts: z.array(
    z.object({
      id: z.string(),
      kind: z.enum(["repayment", "refund", "reversal"]),
      amount,
      unallocated: amount,
      bank_reference: z.string(),
      bank_date: z.string(),
      reversed_by: z.string().nullable(),
    }),
  ),
  movements: z.array(
    z.object({
      id: z.string(),
      action: z.string(),
      reason: z.string(),
      details: z.record(z.string(), z.unknown()),
      created_at: z.string(),
    }),
  ),
})
export const eligibilitySchema = z.object({
  eligible: z.boolean(),
  can_resume: z.boolean().default(false),
  reason: z.string().nullable(),
  amount,
  available: amount,
  remaining: amount,
  term_days: z.number(),
  due_date: z.string().nullable(),
  holder_name: z.string().nullable(),
  holder_rut: z.string().nullable(),
  terms: creditTermsSchema.nullable(),
})
export type CustomerCredit = z.infer<typeof creditSchema>
export const creditAccountResponseSchema = z.object({
  credit: creditSchema.nullable(),
})
export const creditEligibilityResponseSchema = z.object({
  eligibility: eligibilitySchema,
})
export const creditAcceptanceSchema = z.object({
  accepted: z.literal(true),
  terms_version: z.string().min(1),
  accepted_amount: amount.positive(),
  accepted_term_days: z.number().int().min(1).max(365),
})
export const creditCompleteResponseSchema = z.object({
  order_id: z.string().regex(/^order_[a-z0-9_]+$/i),
})
export const creditEmailVerificationSchema = z.object({
  challenge_id: z.string().regex(/^cremail_[a-f0-9]{32}$/),
  code: z.string().regex(/^\d{8}$/, "Ingresa el código de 8 dígitos"),
})
export const creditEmailResponseSchema = z.object({
  verification: z.object({
    challenge_id: z.string(),
    email_hint: z.string(),
    expires_at: z.iso.datetime(),
    resend_at: z.iso.datetime(),
    simulated: z.boolean(),
  }),
})
export type CreditEmailVerification = z.infer<
  typeof creditEmailResponseSchema
>["verification"]
export type CreditEligibility = z.infer<typeof eligibilitySchema>
export const creditMoney = (value: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(value)
export const creditDate = (value: string) =>
  new Intl.DateTimeFormat("es-CL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "America/Santiago",
  }).format(new Date(value.length === 10 ? `${value}T12:00:00Z` : value))
